import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { Audience } from "@front/m19-api-client";
import { IButtonDirective, ISelectMenuImports, MODAL_DATA, ModalRef, ToasterService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { StrategyEx } from "@front/m19-models";
import { StrategyService } from "@front/m19-injection-token";

export interface SelectAudienceModalData {
  strategy: StrategyEx;
  audiences: Audience[];
  organizationId: number;
}

@Component({
  selector: "app-select-audience",
  imports: [ISelectMenuImports, TranslocoDirective, IButtonDirective],
  template: ` <div class="flex flex-col gap-4" *transloco="let t">
    <ISelectMenu
      class="w-full"
      [(value)]="selectedBoostAudience"
      by="audienceId"
      fitTriggerWidth
      [(searchQuery)]="searchQuery"
    >
      <ISelectTrigger class="flex w-full">
        <ISelectValue [placeholder]="t('advanced-settings-modal.select_boost_audience')">
          <div *iSelectValue="let value">{{ value?.audienceName }}</div>
        </ISelectValue>
      </ISelectTrigger>
      <ISelectContent>
        @for (audience of filteredAudiences(); track audience.audienceId) {
          <ISelectOption [value]="audience">
            {{ audience.audienceName }}
          </ISelectOption>
        }
      </ISelectContent>
    </ISelectMenu>
    <div class="flex justify-end gap-2">
      <button iButton variant="secondary" size="sm" (click)="modalRef.close()">{{ t("common.cancel") }}</button>
      <button iButton size="sm" [loading]="loading()" [disabled]="!selectedBoostAudience()" (click)="save()">
        {{ t("common.save") }}
      </button>
    </div>
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectAudienceModalComponent {
  private readonly strategyService = inject(StrategyService);
  private readonly toastrService = inject(ToasterService);

  private readonly data = inject<SelectAudienceModalData>(MODAL_DATA);
  modalRef = inject(ModalRef);

  protected readonly audiences = this.data.audiences;
  protected readonly searchQuery = signal("");
  protected readonly filteredAudiences = computed(() => {
    const query = this.searchQuery().toLowerCase();
    return this.audiences.filter((audience) => !query || (audience.audienceName ?? "").toLowerCase().includes(query));
  });
  protected readonly selectedBoostAudience = signal<Audience | undefined>(this.findSelectedBoostAudience());
  protected readonly loading = signal(false);

  save() {
    if (!this.selectedBoostAudience()) return;
    this.loading.set(true);
    this.strategyService
      .updateStrategyBoostAudience(
        this.data.strategy.accountId,
        this.data.strategy.marketplace,
        this.data.strategy.strategyId!,
        this.data.organizationId,
        this.selectedBoostAudience()!.audienceId!,
      )
      .subscribe({
        next: (strategy: StrategyEx) => {
          this.modalRef.close(strategy);
          this.loading.set(false);
        },
        error: (error) => {
          this.toastrService.error(error?.[0]);
          this.loading.set(false);
        },
      });
  }

  private findSelectedBoostAudience() {
    return this.audiences.find((audience) => audience.audienceId === this.data.strategy.boostAudienceId);
  }
}
